<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { formatMoney, toIntlLocale } from '../helpers';

const props = defineProps({
  deals: { type: Array, default: () => [] },
  stages: { type: Array, default: () => [] },
});
const emit = defineEmits(['openDeal']);

const { t, locale } = useI18n();
const intlLocale = computed(() => toIntlLocale(locale.value));

const today = new Date();
const cursor = ref(new Date(today.getFullYear(), today.getMonth(), 1));

const dayKey = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const todayKey = dayKey(today);

const shiftMonth = step => {
  cursor.value = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth() + step,
    1
  );
};
const goToday = () => {
  cursor.value = new Date(today.getFullYear(), today.getMonth(), 1);
};

// Only the first letter is capitalised ("Agosto de 2026"), not every word.
const monthLabel = computed(() => {
  const label = cursor.value.toLocaleDateString(intlLocale.value, {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
});

// Sunday-first weekday headers rendered in the user's locale.
const weekdays = computed(() =>
  Array.from({ length: 7 }, (_, index) =>
    new Date(2024, 0, 7 + index).toLocaleDateString(intlLocale.value, {
      weekday: 'short',
    })
  )
);

const stageById = computed(() =>
  props.stages.reduce((acc, stage) => {
    acc[stage.id] = stage;
    return acc;
  }, {})
);

const formatTime = date =>
  date.toLocaleTimeString(intlLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
  });

// One flat list of dated entries keyed by day: the deal's expected close date and every
// pending task with a due date.
const eventsByDay = computed(() => {
  const map = {};
  const push = (key, event) => {
    (map[key] ||= []).push(event);
  };

  props.deals.forEach(deal => {
    const color = stageById.value[deal.stageId]?.color || '#64748B';
    if (deal.expectedCloseDate) {
      push(deal.expectedCloseDate, {
        id: `deal-${deal.id}`,
        deal,
        color,
        kind: 'deal',
        time: '',
        title: deal.name,
      });
    }
    (deal.tasks || []).forEach(task => {
      if (task.done || !task.dueAt) return;
      const due = new Date(task.dueAt);
      if (Number.isNaN(due.getTime())) return;
      push(dayKey(due), {
        id: `task-${task.id}`,
        deal,
        color,
        kind: 'task',
        time: formatTime(due),
        title: task.title,
      });
    });
  });

  Object.values(map).forEach(list =>
    list.sort((a, b) => a.time.localeCompare(b.time))
  );
  return map;
});

// Six fixed weeks so the grid height never jumps between months.
const days = computed(() => {
  const first = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth(),
    1
  );
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = dayKey(date);
    return {
      key,
      label: date.getDate(),
      outside: date.getMonth() !== cursor.value.getMonth(),
      isToday: key === todayKey,
      events: eventsByDay.value[key] || [],
    };
  });
});

const monthTotal = computed(() => {
  const deals = days.value
    .filter(day => !day.outside)
    .flatMap(day => day.events)
    .filter(event => event.kind === 'deal');
  return {
    count: deals.length,
    total: formatMoney(
      deals.reduce((sum, event) => sum + (event.deal.amountCents || 0), 0),
      deals[0]?.deal.currency || 'BRL',
      locale.value
    ),
  };
});
</script>

<template>
  <div class="flex flex-col flex-1 gap-3 px-6 pb-6 overflow-hidden">
    <div class="flex items-center gap-3 shrink-0">
      <Button
        icon="i-lucide-chevron-left"
        variant="faded"
        color="slate"
        size="sm"
        @click="shiftMonth(-1)"
      />
      <h3 class="text-base font-medium text-n-slate-12 min-w-48">
        {{ monthLabel }}
      </h3>
      <Button
        icon="i-lucide-chevron-right"
        variant="faded"
        color="slate"
        size="sm"
        @click="shiftMonth(1)"
      />
      <Button
        :label="t('WASAPI_CRM.AGENDA.TODAY')"
        variant="ghost"
        color="slate"
        size="sm"
        @click="goToday"
      />
      <span class="ml-auto text-sm text-n-slate-11">
        {{
          t(
            'WASAPI_CRM.AGENDA.MONTH_SUMMARY',
            { count: monthTotal.count, total: monthTotal.total },
            monthTotal.count
          )
        }}
      </span>
    </div>

    <div
      class="grid grid-cols-7 gap-1 text-xs font-medium text-center capitalize text-n-slate-10 shrink-0"
    >
      <span v-for="weekday in weekdays" :key="weekday">{{ weekday }}</span>
    </div>

    <div class="grid flex-1 grid-cols-7 gap-1 overflow-y-auto auto-rows-fr">
      <div
        v-for="day in days"
        :key="day.key"
        class="flex flex-col gap-1 p-1 border rounded-lg border-n-weak min-h-20"
        :class="[
          day.outside ? 'opacity-40' : '',
          day.isToday ? 'border-n-brand bg-n-alpha-1' : '',
        ]"
      >
        <span
          class="text-xs"
          :class="
            day.isToday ? 'font-semibold text-n-brand' : 'text-n-slate-10'
          "
        >
          {{ day.label }}
        </span>
        <button
          v-for="event in day.events"
          :key="event.id"
          v-tooltip="event.deal.name"
          type="button"
          class="flex items-center gap-1 px-1 py-0.5 text-xs rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="emit('openDeal', event.deal)"
        >
          <span
            v-if="event.kind === 'deal'"
            class="rounded-full size-1.5 shrink-0"
            :style="{ backgroundColor: event.color }"
          />
          <Icon
            v-else
            icon="i-lucide-circle-check"
            class="size-3 shrink-0 text-n-slate-10"
          />
          <span v-if="event.time" class="text-n-slate-10 shrink-0">
            {{ event.time }}
          </span>
          <span class="truncate">{{ event.title }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
